import React, { useState, useEffect, useCallback, useMemo } from 'react';
import UsageEventsApi from '../../services/api/UsageEventsApi';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import './ErrorInvestigationDashboard.css';

const ERROR_EVENT_TYPES = ['APP_ERROR', 'APP_ERROR_BOUNDARY'];
const CHART_COLORS = ['#e94b3c', '#f5a623', '#4a90e2', '#50c878', '#9b59b6', '#1abc9c', '#e74c3c', '#3498db'];
const SEVERITY_OPTIONS = ['critical', 'error', 'warning'];

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const sec = Math.floor((now - d) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

function formatTimestamp(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function parsePayload(event) {
  if (!event) return {};
  if (event.payload) return event.payload;
  if (event.payloadJson) {
    try {
      return typeof event.payloadJson === 'string' ? JSON.parse(event.payloadJson) : event.payloadJson;
    } catch { return {}; }
  }
  return {};
}

const ErrorInvestigationDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [errorEvents, setErrorEvents] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSeverities, setActiveSeverities] = useState(new Set(SEVERITY_OPTIONS));
  const [dateRange, setDateRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 7);
    return {
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0],
    };
  });

  // ─── Fetch ──────────────────────────────────────────

  const fetchAllEvents = useCallback(async ({ eventType, from, to }) => {
    const pageSize = 500;
    const maxPages = 5; // Cap at 5 pages (2,500 events) per event type to limit API load
    let page = 0;
    let totalPages = 1;
    const collected = [];
    while (page < totalPages && page < maxPages && collected.length < 2500) {
      const response = await UsageEventsApi.adminSearch({ eventType, from, to, page, size: pageSize });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();
      const content = Array.isArray(data) ? data : (data.content || data.items || data.results || []);
      collected.push(...content);
      totalPages = Number.isInteger(data.totalPages) ? data.totalPages : (content.length < pageSize ? page + 1 : page + 2);
      page++;
    }
    return collected;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const from = new Date(dateRange.from).toISOString();
      const to = new Date(dateRange.to + 'T23:59:59').toISOString();
      const results = await Promise.all(
        ERROR_EVENT_TYPES.map(eventType => fetchAllEvents({ eventType, from, to }))
      );
      const merged = results.flat().sort((a, b) => new Date(b.occurredAt) - new Date(a.occurredAt));
      setErrorEvents(merged);
    } catch (err) {
      setFetchError(err.message || 'Failed to load error data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, fetchAllEvents]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Computed Data ────────────────────────────────────

  const filteredEvents = useMemo(() => {
    let events = errorEvents;
    // Severity filter
    if (activeSeverities.size < SEVERITY_OPTIONS.length) {
      events = events.filter(ev => {
        const p = parsePayload(ev);
        const sev = p.metadata?.severity || (ev.eventType === 'APP_ERROR_BOUNDARY' ? 'critical' : 'error');
        return activeSeverities.has(sev);
      });
    }
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      events = events.filter(ev => {
        const p = parsePayload(ev);
        const msg = (p.metadata?.errorMessage || p.label || '').toLowerCase();
        const type = (p.metadata?.errorType || p.entityType || '').toLowerCase();
        return msg.includes(q) || type.includes(q);
      });
    }
    return events;
  }, [errorEvents, activeSeverities, searchQuery]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const total = filteredEvents.length;
    const fingerprints = new Set(filteredEvents.map(ev => parsePayload(ev).metadata?.fingerprint || parsePayload(ev).entityId));
    const uniqueErrors = fingerprints.size;
    const users = new Set(filteredEvents.map(ev => ev.userId).filter(Boolean));
    const sessions = new Set(filteredEvents.map(ev => ev.sessionId).filter(Boolean));
    // Errors per hour
    let errorsPerHour = 0;
    if (total > 1) {
      const timestamps = filteredEvents.map(ev => new Date(ev.occurredAt).getTime()).sort();
      const spanMs = timestamps[timestamps.length - 1] - timestamps[0];
      const spanHours = Math.max(spanMs / 3_600_000, 1);
      errorsPerHour = (total / spanHours).toFixed(1);
    }
    return { total, uniqueErrors, affectedUsers: users.size, affectedSessions: sessions.size, errorsPerHour };
  }, [filteredEvents]);

  // Error trend (daily buckets)
  const errorTrend = useMemo(() => {
    const buckets = new Map();
    filteredEvents.forEach(ev => {
      const day = ev.occurredAt?.split('T')[0] || 'unknown';
      buckets.set(day, (buckets.get(day) || 0) + 1);
    });
    return Array.from(buckets.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredEvents]);

  // Error type breakdown (pie)
  const typeBreakdown = useMemo(() => {
    const types = new Map();
    filteredEvents.forEach(ev => {
      const p = parsePayload(ev);
      const t = p.metadata?.errorType || p.entityType || 'Unknown';
      types.set(t, (types.get(t) || 0) + 1);
    });
    return Array.from(types.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [filteredEvents]);

  // Error groups (by fingerprint)
  const errorGroups = useMemo(() => {
    const groups = new Map();
    filteredEvents.forEach(ev => {
      const p = parsePayload(ev);
      const fp = p.metadata?.fingerprint || p.entityId || (p.metadata?.errorMessage || '').slice(0, 100);
      if (!groups.has(fp)) {
        groups.set(fp, {
          fingerprint: fp,
          message: p.metadata?.errorMessage || p.label || 'Unknown error',
          errorType: p.metadata?.errorType || p.entityType || 'Error',
          severity: p.metadata?.severity || (ev.eventType === 'APP_ERROR_BOUNDARY' ? 'critical' : 'error'),
          count: 0,
          users: new Set(),
          sessions: new Set(),
          lastSeen: ev.occurredAt,
          occurrences: [],
        });
      }
      const g = groups.get(fp);
      g.count++;
      if (ev.userId) g.users.add(ev.userId);
      if (ev.sessionId) g.sessions.add(ev.sessionId);
      if (new Date(ev.occurredAt) > new Date(g.lastSeen)) g.lastSeen = ev.occurredAt;
      g.occurrences.push(ev);
    });
    return Array.from(groups.values())
      .map(g => ({ ...g, users: g.users.size, sessions: g.sessions.size, sessionIds: [...groups.get(g.fingerprint).sessions] }))
      .sort((a, b) => b.count - a.count);
  }, [filteredEvents]);

  // ─── Handlers ─────────────────────────────────────────

  const toggleSeverity = (sev) => {
    setActiveSeverities(prev => {
      const next = new Set(prev);
      next.has(sev) ? next.delete(sev) : next.add(sev);
      return next;
    });
  };

  const toggleExpanded = (fp) => {
    setExpandedGroup(prev => prev === fp ? null : fp);
  };

  // ─── Render ───────────────────────────────────────────

  return (
    <div className="error-investigation-dashboard">
      <div className="dashboard-header">
        <h1>Error Investigation</h1>
        <p className="dashboard-subtitle">
          Investigate frontend errors, view stack traces, and analyze error trends across users and sessions
        </p>
      </div>

      {/* Controls */}
      <div className="error-controls">
        <div className="date-field">
          <label>From</label>
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
          />
        </div>
        <div className="date-field">
          <label>To</label>
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
          />
        </div>
        <div className="search-field">
          <label>Search Errors</label>
          <input
            type="text"
            placeholder="Filter by error message or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="severity-filters">
          {SEVERITY_OPTIONS.map(sev => (
            <button
              key={sev}
              className={`severity-pill ${sev}${activeSeverities.has(sev) ? ' active' : ''}`}
              onClick={() => toggleSeverity(sev)}
            >
              {sev}
            </button>
          ))}
        </div>
        <button onClick={fetchData} disabled={loading} className="refresh-btn">
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {fetchError && <div className="error-message">{fetchError}</div>}
      {loading && <div className="loading-spinner">Loading error data...</div>}

      {!loading && filteredEvents.length === 0 && !fetchError && (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <h3>No Errors Found</h3>
          <p>No errors were reported in the selected date range. That&apos;s great!</p>
        </div>
      )}

      {!loading && filteredEvents.length > 0 && (
        <>
          {/* Summary Stats */}
          <div className="stats-overview">
            <div className="stat-card">
              <div className="stat-label">Total Errors</div>
              <div className="stat-value error-count">{summaryStats.total.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Unique Errors</div>
              <div className="stat-value">{summaryStats.uniqueErrors.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Affected Users</div>
              <div className="stat-value">{summaryStats.affectedUsers.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Affected Sessions</div>
              <div className="stat-value">{summaryStats.affectedSessions.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Errors / Hour</div>
              <div className="stat-value">{summaryStats.errorsPerHour}</div>
            </div>
          </div>

          {/* Charts Row: Trend + Type Breakdown */}
          <div className="chart-grid">
            <div className="chart-container">
              <h2>Error Trend</h2>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={errorTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" name="Errors" stroke="#e94b3c" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-container">
              <h2>Error Type Breakdown</h2>
              {typeBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={typeBreakdown}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {typeBreakdown.map((_, idx) => (
                        <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>No data</p>
              )}
            </div>
          </div>

          {/* Error Groups Table */}
          <div className="section-card">
            <h2>Error Groups ({errorGroups.length})</h2>
            <div style={{ overflowX: 'auto' }}>
              <table className="error-groups-table">
                <thead>
                  <tr>
                    <th>Error Message</th>
                    <th>Type</th>
                    <th>Severity</th>
                    <th>Count</th>
                    <th>Users</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {errorGroups.map(group => (
                    <React.Fragment key={group.fingerprint}>
                      <tr
                        className={expandedGroup === group.fingerprint ? 'expanded' : ''}
                        onClick={() => toggleExpanded(group.fingerprint)}
                      >
                        <td>
                          <div className="error-group-message" title={group.message}>
                            {group.message}
                          </div>
                        </td>
                        <td><span className="error-type-badge">{group.errorType}</span></td>
                        <td><span className={`severity-badge ${group.severity}`}>{group.severity}</span></td>
                        <td><span className="error-count-badge">{group.count}</span></td>
                        <td>{group.users}</td>
                        <td><span className="time-ago" title={formatTimestamp(group.lastSeen)}>{timeAgo(group.lastSeen)}</span></td>
                      </tr>

                      {/* Expanded Detail */}
                      {expandedGroup === group.fingerprint && (
                        <tr className="error-detail-row">
                          <td colSpan={6}>
                            <ErrorDetailPanel group={group} navigate={navigate} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Error Detail Subcomponent ─────────────────────────

function ErrorDetailPanel({ group, navigate }) {
  // Take the most recent occurrence for the primary stack trace display
  const latest = group.occurrences[0];
  const latestPayload = parsePayload(latest);
  const meta = latestPayload.metadata || {};

  return (
    <div className="error-detail-content">
      {/* Stack Trace */}
      {meta.stackTrace && (
        <div className="stack-trace-section">
          <h4>Stack Trace</h4>
          <pre className="stack-trace-viewer">{meta.stackTrace}</pre>
        </div>
      )}

      {/* Component Stack (for error boundary catches) */}
      {meta.componentStack && (
        <div className="stack-trace-section">
          <h4>Component Stack</h4>
          <pre className="component-stack-viewer">{meta.componentStack}</pre>
        </div>
      )}

      {/* Breadcrumbs */}
      {meta.breadcrumbs && meta.breadcrumbs.length > 0 && (
        <div className="breadcrumbs-section">
          <h4>Breadcrumbs (actions before error)</h4>
          <div className="breadcrumbs-timeline">
            {meta.breadcrumbs.map((bc, i) => (
              <div key={i} className="breadcrumb-item">
                <span className="breadcrumb-time">
                  {bc.timestamp ? new Date(bc.timestamp).toLocaleTimeString() : '--'}
                </span>
                <span className="breadcrumb-category">{bc.category}</span>
                <span className="breadcrumb-message">{bc.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Browser Context */}
      {meta.browser && (
        <div className="stack-trace-section">
          <h4>Browser Context</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', fontSize: '13px' }}>
            <div><strong>URL:</strong> {meta.browser.url}</div>
            <div><strong>Route:</strong> {meta.browser.route}</div>
            <div><strong>Viewport:</strong> {meta.browser.viewportWidth}×{meta.browser.viewportHeight}</div>
            <div><strong>Capture Source:</strong> {meta.captureSource}</div>
          </div>
        </div>
      )}

      {/* Affected Sessions */}
      {group.sessionIds && group.sessionIds.length > 0 && (
        <div className="stack-trace-section">
          <h4>Affected Sessions ({group.sessions})</h4>
          <div className="sessions-list">
            {group.sessionIds.slice(0, 20).map(sid => (
              <span
                key={sid}
                className="session-link"
                onClick={(e) => { e.stopPropagation(); navigate(`/settings/session-journey/${sid}`); }}
              >
                {sid.slice(0, 8)}…
              </span>
            ))}
            {group.sessionIds.length > 20 && (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)', alignSelf: 'center' }}>
                +{group.sessionIds.length - 20} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Recent Occurrences */}
      {group.occurrences.length > 1 && (
        <div className="occurrences-section">
          <h4>Recent Occurrences ({group.occurrences.length})</h4>
          <table className="occurrences-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Session</th>
                <th>Source</th>
                <th>URL</th>
              </tr>
            </thead>
            <tbody>
              {group.occurrences.slice(0, 10).map((occ, i) => {
                const p = parsePayload(occ);
                const m = p.metadata || {};
                return (
                  <tr key={i}>
                    <td>{formatTimestamp(occ.occurredAt)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{(occ.sessionId || '').slice(0, 8)}…</td>
                    <td>{m.captureSource || '-'}</td>
                    <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.browser?.url || m.browser?.route || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {group.occurrences.length > 10 && (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
              Showing 10 of {group.occurrences.length} occurrences
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ErrorInvestigationDashboard;
